'use client';

import { Box, Typography, Paper, Button, Stack, CircularProgress } from '@mui/material';
import { lusitana } from '@/components/app/proyectos/raceway/fonts';

type ValvulaItem = {
  id: string;         // 'v1', 'v2', etc.
  label: string;      // descripcion corta
  abierta: boolean;   // estado actual via WebSocket
  bloqueada?: boolean;
};

type FaseCardProps = {
  fase: number;               // 1, 2, 3
  deposito: string;           // nombre del deposito
  color: string;              // clases de gradiente tailwind (ej: "from-blue-800 to-blue-700")
  valvulas: ValvulaItem[];
  vaciadoManual?: boolean;    // si tiene indicador de vaciado manual
  onValvula: (id: string, estadoActual: boolean) => void;
  cargando: string | null;    // id de la valvula en proceso
};

// Mapeo de gradientes de Tailwind a CSS nativo
const gradientMap: Record<string, string> = {
  'from-blue-800 to-blue-700': 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
  'from-blue-600 to-blue-500': 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
  'from-blue-400 to-blue-300': 'linear-gradient(135deg, #60a5fa 0%, #93c5fd 100%)',
};

export default function FaseCard({
  fase, deposito, color, valvulas, vaciadoManual, onValvula, cargando
}: FaseCardProps) {
  
  const bgGradient = gradientMap[color] || color;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '16px',
        background: bgGradient,
        p: 3, // ← Aumentado de 2 a 3
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        width: '100%', // ← Asegurar que ocupe todo el ancho disponible
        minWidth: 280, // ← Ancho mínimo para pantallas pequeñas
      }}
    >
      {/* Número decorativo de fondo - ajustado posición */}
      <Typography
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: 16, // ← Aumentado de 12 a 16
          top: 8, // ← Ajustado
          fontSize: '5rem', // ← Aumentado de 4rem a 5rem
          fontWeight: 'bold',
          color: 'rgba(255, 255, 255, 0.05)',
          userSelect: 'none',
          lineHeight: 1
        }}
      >
        {fase}
      </Typography>

      {/* Cabecera */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          color: 'rgba(255, 255, 255, 0.6)',
          display: 'block',
          fontSize: '0.75rem', // ← Tamaño fijo
        }}
      >
        Fase {fase}
      </Typography>
      
      <Typography
        className={lusitana.className}
        sx={{
          mb: 2.5, // ← Aumentado de 2 a 2.5
          mt: 0.5,
          fontSize: '1rem', // ← Aumentado de 0.875rem a 1rem
          fontWeight: 'bold',
          color: 'white'
        }}
      >
        {deposito}
      </Typography>

      {/* Válvulas */}
      <Stack spacing={1.5}> {/* ← Aumentado spacing de 1 a 1.5 */}
        {valvulas.map((v) => {
          const enCarga = cargando === v.id;
          
          return (
            <Box
              key={v.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                px: 2, // ← Aumentado de 1.5 a 2
                py: 1.5, // ← Aumentado de 1 a 1.5
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> {/* ← Aumentado gap de 1.5 a 2 */}
                {/* Indicador de estado */}
                <Box
                  sx={{
                    height: 10, // ← Aumentado de 8 a 10
                    width: 10, // ← Aumentado de 8 a 10
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: v.bloqueada ? '#d1d5db' : v.abierta ? '#4ade80' : '#f87171',
                    animation: (!v.bloqueada && v.abierta) ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                      '100%': { opacity: 1, transform: 'scale(1)' },
                    }
                  }}
                />
                <Box>
                  <Typography sx={{ 
                    fontSize: '0.8rem', // ← Aumentado de 0.7rem a 0.8rem
                    fontWeight: 'bold', 
                    color: 'white', 
                    textTransform: 'uppercase' 
                  }}>
                    {v.id}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '0.75rem', // ← Aumentado de 0.7rem a 0.75rem
                    color: 'rgba(255, 255, 255, 0.6)', 
                    lineHeight: 1.2 
                  }}>
                    {v.label}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                size="small"
                disabled={v.bloqueada || enCarga}
                onClick={() => onValvula(v.id, v.abierta)}
                sx={{
                  ml: 2, 
                  minWidth: '75px', 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  boxShadow: 'none',
                  py: 0.75, 
                  bgcolor: v.abierta ? 'rgba(239, 68, 68, 0.7)' : 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: v.abierta ? '#ef4444' : 'rgba(255, 255, 255, 0.3)',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                {enCarga ? <CircularProgress size={14} color="inherit" /> : v.abierta ? 'Cerrar' : 'Abrir'}
              </Button>
            </Box>
          );
        })}

        {/* Vaciado manual */}
        {vaciadoManual && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2, 
              borderRadius: '12px',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              px: 2, 
              py: 1.5, 
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography sx={{ fontSize: '1rem' }}>🔧</Typography> 
            <Box>
              <Typography sx={{ 
                fontSize: '0.75rem', 
                fontWeight: 'bold', 
                color: 'rgba(255, 255, 255, 0.8)' 
              }}>
                Vaciado
              </Typography>
              <Typography sx={{ 
                fontSize: '0.7rem', 
                color: 'rgba(255, 255, 255, 0.5)', 
                lineHeight: 1.2 
              }}>
                Operación manual
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}